'use client';
import { useMutation, useSuspenseQuery, useQuery } from '@tanstack/react-query';
import { PRODUCT_MUTATIONS, PRODUCT_QUERIES } from '../../infrastructure/product/utils/queries';

import type {
  ProductsAllRequest,
  ProductsCollectionRequest,
  ProductsByIdsRequest,
  TopRankedProductsRequest,
  ProductsCollectionCompetitorRequest,
  ProductBrowseRequest,
  ProductsDetailRequest,
  ProductsDetailResponse,
  ProductMetaRequest,
  ProductMetaResponse,
  ProductSearchRequest,
} from '../../infrastructure/product/utils/types';

export const useProductService = () => {
  const useGetProductsAll = (params: ProductsAllRequest) => {
    return useSuspenseQuery(PRODUCT_QUERIES.getProductsAll(params));
  };

  const useGetProductsCollections = (params: ProductsCollectionRequest) => {
    return useSuspenseQuery(PRODUCT_QUERIES.getProductsCollections(params));
  };
  const useGetProductsCollectionsQuery = (params: ProductsCollectionRequest) => {
    return useQuery(PRODUCT_QUERIES.getProductsCollections(params));
  };

  const useGetProductsByIds = (params: ProductsByIdsRequest) => {
    return useSuspenseQuery(PRODUCT_QUERIES.getProductsByIds(params));
  };

  const useGetProductsByIdsQuery = (params: ProductsByIdsRequest) => {
    return useQuery(PRODUCT_QUERIES.getProductsByIds(params));
  };

  const useGetProductsDetail = (params: ProductsDetailRequest) => {
    return useSuspenseQuery<ProductsDetailResponse>(PRODUCT_QUERIES.getProductsDetail(params));
  };

  const useGetProductsDetailWithQuery = (params: ProductsDetailRequest) => {
    return useQuery<ProductsDetailResponse>(PRODUCT_QUERIES.getProductsDetail(params));
  };

  const useGetProductMeta = (params: ProductMetaRequest) => {
    return useSuspenseQuery<ProductMetaResponse>(PRODUCT_QUERIES.getProductMeta(params));
  };

  const useGetProductsByIdsMutation = () => {
    return useMutation(PRODUCT_MUTATIONS.getProductsByIds());
  };

  const useGetTopRankedProducts = (params: TopRankedProductsRequest) => {
    return useSuspenseQuery(PRODUCT_QUERIES.getTopRankedProducts(params));
  };

  const useGetTopRankedProductsQuery = (params: TopRankedProductsRequest) => {
    return useQuery(PRODUCT_QUERIES.getTopRankedProducts(params));
  };

  const useGetProductTaxonomy = (options?: { enabled?: boolean }) => {
    return useQuery({
      ...PRODUCT_QUERIES.getProductTaxonomy(),
      ...options,
    });
  };

  const useGetSuspenseProductTaxonomy = () => {
    return useSuspenseQuery(PRODUCT_QUERIES.getProductTaxonomy());
  };

  const useGetProductBrandTaxonomy = () => {
    return useSuspenseQuery(PRODUCT_QUERIES.getProductBrandTaxonomy());
  };

  const useGetProductsCollectionCompetitor = (params: ProductsCollectionCompetitorRequest) => {
    return useSuspenseQuery(PRODUCT_QUERIES.getProductsCollectionCompetitor(params));
  };

  const useGetCategoryTaxonomy = () => {
    return useSuspenseQuery(PRODUCT_QUERIES.getCategoryTaxonomy());
  };

  const useGetProductBrowse = (params?: ProductBrowseRequest) => {
    return useSuspenseQuery(PRODUCT_QUERIES.getProductBrowse(params));
  };

  const useGetProductSearch = (params: ProductSearchRequest) => {
    return useSuspenseQuery(PRODUCT_QUERIES.getProductSearch(params));
  };

  return {
    useGetProductsAll,
    useGetProductsCollections,
    useGetProductsCollectionsQuery,
    useGetProductsByIds,
    useGetProductsDetail,
    useGetProductMeta,
    useGetProductsByIdsMutation,
    useGetTopRankedProducts,
    useGetTopRankedProductsQuery,
    useGetSuspenseProductTaxonomy,
    useGetProductTaxonomy,
    useGetProductBrandTaxonomy,
    useGetProductsCollectionCompetitor,
    useGetCategoryTaxonomy,
    useGetProductBrowse,
    useGetProductSearch,
    useGetProductsDetailWithQuery,
    useGetProductsByIdsQuery,
  };
};
